import { useCallback, useRef, useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '@/store/hooks';
import { setFilePanelWidth } from '@/store/slices/uiSlice';
import { addFile } from '@/store/slices/filesSlice';
import { cn } from '@/lib/utils';
import { ProjectSelector } from './ProjectSelector';
import { FileList } from './FileList';
import { EditorPanel } from './EditorPanel';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { PanelLeft } from 'lucide-react';
import { FileDropZone } from '@/components/editor/FileDropZone';
import { toast } from 'sonner';
import type { File } from '@phrasewright/types';
import type { StorageFile } from '@/data/adapters/types';

interface MainLayoutProps {
  className?: string;
  onStartOnboarding?: () => void;
}

export function MainLayout({ className, onStartOnboarding }: MainLayoutProps) {
  const dispatch = useAppDispatch();
  const { filePanelWidth } = useAppSelector((state) => state.ui.layout);
  const [isResizing, setIsResizing] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [mobileSheetOpen, setMobileSheetOpen] = useState(false);
  const resizeRef = useRef<HTMLDivElement>(null);

  // Check if we're on mobile
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768); // md breakpoint
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle resizing of the file panel
  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth = filePanelWidth;

      const handleMouseMove = (e: MouseEvent) => {
        const diff = e.clientX - startX;
        const newWidth = Math.max(200, Math.min(600, startWidth + diff));
        dispatch(setFilePanelWidth(newWidth));
      };

      const handleMouseUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [dispatch, filePanelWidth]
  );

  // Convert StorageFile to core File type
  const convertToFile = (storageFile: StorageFile): File => ({
    id: storageFile.id,
    name: storageFile.name,
    path: `/${storageFile.type === 'phrase' ? 'phrases' : 'documents'}/${storageFile.name}`,
    content: storageFile.content,
    metadata: {
      type: storageFile.type,
      tags: storageFile.tags,
      ...storageFile.metadata,
    },
    revisions: [],
    createdAt: storageFile.createdAt,
    updatedAt: storageFile.updatedAt,
    status: 'active',
    projectId: storageFile.projectId,
  });

  const FileBrowserContent = () => (
    <FileDropZone
      context="SIDEBAR"
      className="h-full"
      showIndicators={true}
      onImport={(files) => {
        // Convert and add imported files to the store
        files.forEach((storageFile) => {
          const file = convertToFile(storageFile);
          dispatch(addFile(file));
        });
        toast.success(
          `Imported ${files.length} ${files.length === 1 ? 'file' : 'files'}`
        );
      }}
    >
      <ProjectSelector />
      <Separator />
      <FileList className="flex-1" />
    </FileDropZone>
  );

  if (isMobile) {
    // Mobile Layout with Sheet
    return (
      <div className={cn('flex h-full w-full bg-background', className)}>
        {/* Mobile File Browser Button */}
        <div className="absolute top-4 left-4 z-10">
          <Sheet open={mobileSheetOpen} onOpenChange={setMobileSheetOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon" className="h-8 w-8">
                <PanelLeft className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="left" className="w-80 p-0">
              <div className="flex flex-col h-full">
                <FileBrowserContent />
              </div>
            </SheetContent>
          </Sheet>
        </div>

        {/* Full Width Editor */}
        <div className="flex-1 flex flex-col">
          <EditorPanel onStartOnboarding={onStartOnboarding} />
        </div>
      </div>
    );
  }

  // Desktop Layout
  return (
    <div
      className={cn(
        'flex h-full w-full bg-background overflow-hidden',
        className
      )}
    >
      {/* Left Sidebar - Project Selector + File Browser */}
      <div
        className="hidden md:flex flex-col border-r bg-card/50 backdrop-blur-sm"
        style={{ width: filePanelWidth }}
      >
        <FileBrowserContent />
      </div>

      {/* Resize Handle */}
      <div
        ref={resizeRef}
        className={cn(
          'hidden md:block w-1 bg-border hover:bg-accent cursor-col-resize',
          'hover:w-2 transition-all duration-200',
          isResizing && 'bg-accent w-2'
        )}
        onMouseDown={handleResizeStart}
        role="separator"
        aria-orientation="vertical"
        aria-label="Resize file panel"
      />

      {/* Right Panel - Editor */}
      <FileDropZone
        context="EDITOR_NEW"
        className="flex-1 flex flex-col min-w-0"
        showIndicators={true}
        onImport={(files) => {
          // Convert and add imported files, select the first one
          files.forEach((storageFile, index) => {
            const file = convertToFile(storageFile);
            dispatch(addFile(file));
            if (index === 0) {
              // Select the first imported file
              window.dispatchEvent(
                new CustomEvent('select-file', {
                  detail: { fileId: file.id },
                })
              );
            }
          });
          toast.success(
            `Created ${files.length} new ${files.length === 1 ? 'file' : 'files'}`
          );
        }}
      >
        <EditorPanel onStartOnboarding={onStartOnboarding} />
      </FileDropZone>
    </div>
  );
}
